import type {
  TreeChildEdge,
  TreeNodeDefinition,
  TreeParams,
  TreePath,
} from '../../../probability/tree/tree/tree.model';

export interface DigitRestriction {
  label: string;
  allowedDigits: number[];
}

export interface PinCodeWidgetParams {
  digitCount: number;
  allowRepeats: boolean;
  restrictions?: Record<number, DigitRestriction>;
}

export interface PinCodeTreeResult {
  tree: TreeParams;
  selectedPath: TreePath;
}

export interface DigitColumn {
  position: number;
  label: string;
  restrictionLabel?: string;
  choices: number;
  digits: number[];
  selectedDigit: number | null;
  disabledDigits: Set<number>;
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function getAvailableDigits(
  position: number,
  allowRepeats: boolean,
  selections: Array<number | null>,
  restrictions?: Record<number, DigitRestriction>,
): { digits: number[]; disabledDigits: Set<number>; restrictionLabel?: string } {
  const allDigits = Array.from({ length: 10 }, (_, d) => d);
  const restriction = restrictions?.[position];
  const allowedSet = restriction ? new Set(restriction.allowedDigits) : null;

  const disabledDigits = new Set<number>();

  if (allowedSet) {
    for (const d of allDigits) {
      if (!allowedSet.has(d)) {
        disabledDigits.add(d);
      }
    }
  }

  if (!allowRepeats) {
    for (let j = 0; j < position; j++) {
      const sel = selections[j];
      if (sel !== null && sel !== undefined) {
        disabledDigits.add(sel);
      }
    }
  }

  return {
    digits: allDigits,
    disabledDigits,
    restrictionLabel: restriction?.label,
  };
}

export function computeColumns(
  digitCount: number,
  allowRepeats: boolean,
  selections: Array<number | null>,
  restrictions?: Record<number, DigitRestriction>,
): DigitColumn[] {
  return Array.from({ length: digitCount }, (_, i) => {
    const { digits, disabledDigits, restrictionLabel } = getAvailableDigits(i, allowRepeats, selections, restrictions);
    const availableCount = digits.filter((d) => !disabledDigits.has(d)).length;

    return {
      position: i,
      label: ordinal(i + 1),
      restrictionLabel,
      choices: availableCount,
      digits,
      selectedDigit: selections[i] ?? null,
      disabledDigits,
    };
  });
}

/**
 * Build a tree that progressively expands as the user selects digits.
 *
 * - Each level shows only the digits allowed by restrictions (if any).
 * - Only the selected branch at each depth is expanded to the next level.
 * - When `allowRepeats` is false, already-selected digits are removed from
 *   subsequent levels.
 *
 * Returns both the `TreeParams` and the `selectedPath` (child indices) for
 * highlighting the user's current selection through the tree.
 */
export function buildPinCodeTree(
  digitCount: number,
  allowRepeats: boolean,
  selections: Array<number | null>,
  restrictions?: Record<number, DigitRestriction>,
): PinCodeTreeResult {
  const selectedPath: number[] = [];

  function buildLevel(depth: number, usedDigits: Set<number>): TreeChildEdge[] | undefined {
    if (depth >= digitCount) {
      return undefined;
    }

    const restriction = restrictions?.[depth];
    const baseDigits = restriction ? restriction.allowedDigits : Array.from({ length: 10 }, (_, d) => d);
    const available = baseDigits.filter((d) => allowRepeats || !usedDigits.has(d));

    const selected = selections[depth];

    if (selected !== null && selected !== undefined) {
      const childIndex = available.indexOf(selected);
      if (childIndex >= 0) {
        selectedPath.push(childIndex);
      }
    }

    return available.map((d) => {
      const isSelected = d === selected;
      const nextUsed = new Set(usedDigits);
      if (!allowRepeats) {
        nextUsed.add(d);
      }

      const children = isSelected ? buildLevel(depth + 1, nextUsed) : undefined;

      const node: TreeNodeDefinition = { id: `d${depth}-${d}`, children };
      return { label: String(d), node };
    });
  }

  const root: TreeNodeDefinition = {
    id: 'root',
    children: buildLevel(0, new Set()),
  };

  return {
    tree: { root, display: { nodeRadius: 6, showLeafEdgeLabels: true, zoomEnabled: false, hoverEnabled: false } },
    selectedPath,
  };
}
